import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import useTheme from '@/hooks/useTheme';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/how-it-works', label: 'How it works', icon: 'ri-book-open-line' },
  { path: '/strategies', label: 'Strategies', icon: 'ri-brain-line' },
  { path: '/patterns', label: 'Patterns', icon: 'ri-book-2-line' },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';

  const { theme, toggle: toggleTheme, isLight } = useTheme();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (isLoginPage) {
    return <>{children}</>;
  }

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        ></div>
      )}

      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-3 left-3 z-50 w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 hover:text-foreground-900 transition-all cursor-pointer md:hidden"
      >
        <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
      </button>

      <aside className={`${collapsed ? 'w-16' : 'w-56'} fixed md:relative z-50 h-full flex flex-col bg-background-100 border-r border-background-200/70 transition-all duration-300 shrink-0 md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center h-14 px-4 border-b border-background-200/70">
          {!collapsed && (
            <a
              href="https://vietnamist.xyz"
              className="flex items-center gap-2 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-md bg-primary-500 flex items-center justify-center shrink-0">
                <i className="ri-flashlight-line text-background-50 text-sm"></i>
              </div>
              <span className="font-brand font-bold text-foreground-950 text-sm whitespace-nowrap">Chín Hồng Mao</span>
            </a>
          )}
          {collapsed && (
            <a
              href="https://vietnamist.xyz"
              className="w-7 h-7 rounded-md bg-primary-500 flex items-center justify-center mx-auto shrink-0 cursor-pointer"
            >
              <i className="ri-flashlight-line text-background-50 text-sm"></i>
            </a>
          )}
        </div>

        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/50'
                }`}
              >
                <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-2 border-t border-background-200/70">
          {!collapsed && (
            <div className="px-3 py-1.5 mb-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-accent-500/10 text-accent-400 border-accent-500/20">
                Super Admin
              </span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full items-center justify-center py-2 rounded-lg text-foreground-500 hover:text-foreground-800 hover:bg-background-200/50 transition-all cursor-pointer hidden md:flex"
          >
            <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-lg`}></i>
          </button>
          <button
            onClick={toggleTheme}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-600 hover:text-foreground-900 hover:bg-background-200/50 transition-all cursor-pointer text-sm ${collapsed ? 'justify-center' : ''}`}
          >
            <i className={`${isLight ? 'ri-sun-line' : 'ri-moon-line'} text-base w-5 h-5 flex items-center justify-center`}></i>
            {!collapsed && <span className="whitespace-nowrap">{isLight ? 'Light' : 'Dark'}</span>}
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-600 hover:text-red-400 hover:bg-background-200/50 transition-all cursor-pointer text-sm ${collapsed ? 'justify-center' : ''}`}
          >
            <i className="ri-logout-box-line text-base w-5 h-5 flex items-center justify-center"></i>
            {!collapsed && <span className="whitespace-nowrap">Logout</span>}
          </button>
        </div>
      </aside>

      <main key={location.pathname} className="flex-1 overflow-y-auto bg-background-50 pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
}